import { useEffect, useRef } from 'react';

import { findCustomRendererKey, getCustomisationFieldData, getGroupData } from "../../EditablePreview/EditablePreview";
import AudioField from "./audio/AudioField";
import ColorPickerField from "./colorPicker/ColorPickerField";
import DisruptorField from "./disruptor/DisruptorField";
import DropdownField from "./dropdown/DropdownField";
import EditDataButton from "./edit-data/EditDataButton";
import FontSelectField from "./font-select/FontSelect";
import HmsTimeSelect from "./hmsTimeSelect/HmsTimeSelect";
import ImageField from "./image/ImageField";
import PreviewImageField from "./image/PreviewImageField";
import MinSecTimeSelect from "./minSecTimeSelect/MinSecTimeSelect";
import NumberField from "./numberField/NumberField";
import RangeSliderField from "./rangeSlider/RangeSliderField";
import RelHmsTimeSelect from "./relHmsTimeSelect/RelHmsTimeSelect";
import NoteField from "./stringField/NoteField";
import ReadOnlyField from "./stringField/ReadOnlyField";
import StringField from "./stringField/StringField";
import SpamHashtagField from "./spam/SpamHashtagField";
import TextAreaField from "./stringField/TextAreaField";
import ToggleField from "./toggle/ToggleField";
import VideoField from "./video/VideoField";

const getFieldLinkTail = (fieldLink) => {
    if (typeof fieldLink !== 'string' || !fieldLink.length) return '';
    return fieldLink.split('.').slice(-2).join('.');
};

const matchesSelectedFieldLink = (selectedFieldLink, candidateFieldLink) => {
    if (typeof selectedFieldLink !== 'string' || typeof candidateFieldLink !== 'string') return false;
    if (selectedFieldLink === candidateFieldLink) return true;
    const selectedTail = getFieldLinkTail(selectedFieldLink);
    const candidateTail = getFieldLinkTail(candidateFieldLink);
    if (selectedTail && candidateTail && selectedTail === candidateTail) return true;
    const selectedLast = selectedFieldLink.split('.').pop();
    const candidateLast = candidateFieldLink.split('.').pop();
    return !!selectedLast && selectedLast === candidateLast;
};

const findVal = (valsObject, varName, tabKey = null, altValsObject = null, index = -1) => {
    // console.log('--- Finding val: ', valsObject, varName);
    const varNameParts = varName.split('.');
    let ousideTabVal = null;
    for (let tab in valsObject) {
        for (let group in valsObject[tab]) {
            for (let subGroup in valsObject[tab][group]) {
                for (let i = 0; i < valsObject[tab][group][subGroup].length; i++) {
                    if (index === -1 || i === index || valsObject[tab][group][subGroup].length === 1) {
                        for (let valName in valsObject[tab][group][subGroup][i]) {
                            if (valName === varNameParts[varNameParts.length - 1]) {
                                // console.log('Found match!');
                                // The names match!
                                if (varNameParts.length === 1) {
                                    // We only supplied the val name, so let's assume it is unique
                                    return valsObject[tab][group][subGroup][i][valName];
                                }
                                // check if the path to the value (or part path) matches what was supplied
                                let pathMatchCheck = "";
                                if (varNameParts.length === 4) {
                                    pathMatchCheck += tab + '.';
                                }
                                if (varNameParts.length >= 3) {
                                    pathMatchCheck += group + '.';
                                }
                                if (varNameParts.length >= 2) {
                                    pathMatchCheck += subGroup + '.';
                                }
                                pathMatchCheck += valName;
                                // console.log('Path to match: ', pathMatchCheck);
                                if (pathMatchCheck === varName) {
                                    if (tab === tabKey) {
                                        // console.log('Val in same tab!');
                                        return valsObject[tab][group][subGroup][i][valName];
                                    } else {
                                        // Remember as a first possible match, but keep looking for a match in the same tab
                                        if (ousideTabVal === null) {
                                            ousideTabVal = valsObject[tab][group][subGroup][i][valName];
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    if (ousideTabVal !== null) {
        // console.log('Val outside tab: ', ousideTabVal);
        return ousideTabVal;
    }

    if (altValsObject) {
        return findVal(altValsObject, varName, tabKey);
    }

    return undefined;
};

const evaluateDisplayConditions = (displayConditions, getValFunc) => {
    let displayControl = true;
    if (displayConditions && displayConditions.length > 0) {
        // console.log('>>>> EVALING CONDITIONS: ', displayConditions);
        const condArr = displayConditions;
        for (let condI = 0; condI < displayConditions.length; condI++) {
            const condVal = displayConditions[condI];
            // console.log('--->>>> EVALIING CONDITION: ', condVal);
        // displayConditions.forEach(
            // (condVal, condI, condArr) => {
                if (displayControl) {
                    // We hit an OR, so we can skip the rest of the conditions if we already evaluated to true
                    if (condVal.evalCondition === 'OR' && displayConditions.length > condI + 1) {
                        // console.log('HIT OR! Return ', displayControl);
                        return displayControl;
                    }
                    const displayConditionVal = getValFunc(condVal);
                    if (typeof displayConditionVal !== 'undefined') {
                        const safeDisplayConditionVal = String(displayConditionVal).replace(/'/g, "");
                        const safeCondVal = String(condVal.val).replace(/'/g, "");
                        // Anything that is not a number needs to be enclosed in quotes in our eval string (so we can make > and < work and also arbitrary strings like "none")
                        const evalStr = typeof condVal.val === 'number' && typeof displayConditionVal === 'number' ?
                            String(displayConditionVal) + condVal.evalCondition + String(condVal.val) :
                            "'" + safeDisplayConditionVal + "'" + condVal.evalCondition + "'" + safeCondVal + "'";
                        // console.log('Eval string: ', evalStr, eval?.(evalStr));
                        displayControl = eval?.(evalStr);
                    }
                } else {
                    // We have evaluated to false, but we hit an OR, so we need to reset the display control to true if there are more conditions to evaluate
                    if (condVal.evalCondition === 'OR' && displayConditions.length > condI + 1) {
                        // console.log('HIT OR! Reset displayControl to true and evaluate next block...');
                        displayControl = true;
                    }
                }
            //}
        // );
        }
    }
    return displayControl;
};

export const evaluateDisplayConditionsForListNode = (node, campaignData) => {
    if (node?.config?.groupSettings?.dataLink && node?.featureData?.feature?._id) {
        const groupSchema = getCustomisationFieldData(campaignData, node.featureData._id, node.config.groupSettings.dataLink);
        // console.log('Evaluating display conditions for node:', node, 'with group schema:', groupSchema);
        if (groupSchema?.displayCondition && Array.isArray(groupSchema.displayCondition)) {
            return evaluateDisplayConditions(groupSchema.displayCondition, 
                (condVal) => {
                    // console.log('Evaluating: Getting val for condition:', condVal);
                    const val = findVal(node.customisationVars, condVal.var, null, null, 0);
                    if (typeof val !== 'undefined' && val !== null) {
                        // console.log('Evaluating: Found val:', val);
                        return val;
                    }
                    return null;
                }
            );
        }
    }
    return true;
}

/**
 * Evaluate the display conditions for a field
 * @param {*} props Should be the props passed in to a module or field, should have customisationVars, campaignData, selectedFeatureNode, index, visualisationComponentKey
 * @param {*} fieldDataIn Can be a string path to the customisation data or and object with a dataLink property or and object with a customFieldConfig.dataLink property
 * @returns 
 */
export const evaluateDisplayConditionsForField = (props, fieldDataIn) => {
    // console.log('Evaluating display conditions for field:', props, fieldDataIn);
    if (fieldDataIn.customFieldConfig?.isShown === false || (fieldDataIn.customFieldConfig?.fixedValue !== null && typeof fieldDataIn.customFieldConfig?.fixedValue !== 'undefined')) {
        return false;
    }
    const customDisplayCondition = fieldDataIn.customFieldConfig?.displayCondition;
    const fieldDataLink = fieldDataIn?.customFieldConfig?.dataLink ?? fieldDataIn?.dataLink;
    // Prefer the sibling values from this exact record. A bare name such as
    // "playSFX" is not globally unique when a feature has several winners.
    const fieldFamilyData = fieldDataLink
        ? getGroupData(props.customisationVars, fieldDataLink, props.index ?? 0, true)
        : null;
    if (Array.isArray(customDisplayCondition) && !evaluateDisplayConditions(customDisplayCondition, (condVal) =>
        fieldFamilyData?.[condVal.var] ?? findVal(props.customisationVars, condVal.var, null, null, props.index ?? 0)
    )) {
        return false;
    }
    // Canvas-specific display rules deliberately replace legacy schema rules
    // here. For example, the legacy Spam Scores field also expects the retired
    // recordSpamTags flag, while the canvas only needs the board type to be spam.
    if (Array.isArray(customDisplayCondition)) {
        return true;
    }
    let shouldDisplay = true;
    // Split the dataLink and extract the customisation schema data for each step and log it out
    const dataLink = typeof fieldDataIn === 'string' ? fieldDataIn : fieldDataIn?.dataLink ?? fieldDataIn?.customFieldConfig?.dataLink;
    if (dataLink) {
        if (!props.selectedFeatureNode?.featureData) {
            return true;
        }
        const familyData = getGroupData(props.customisationVars, dataLink, props.index ?? 0, true) || {};
        const dataLinkParts = dataLink.split('.');
        for (let i = 0; i < dataLinkParts.length; i++) {
            let path = '';
            for (let j = 0; j <= i; j++) {
                path += (j > 0 ? '.' : '') + dataLinkParts[j];
            }
            // console.log('CustomFieldRenderer DataLink Part:', path, getCustomisationFieldData(props.campaignData, props.selectedFeatureNode.featureData._id, path, props.index ?? 0, props.visualisationComponentKey ?? null));
            const fieldDataSchema = getCustomisationFieldData(props.campaignData, props.featureId || props.selectedFeatureNode.featureData._id, path, props.index ?? 0, props.visualisationComponentKey ?? null);
            // Check for any display conditions on this field
            if (fieldDataSchema?.displayCondition && Array.isArray(fieldDataSchema.displayCondition)) {
                shouldDisplay = evaluateDisplayConditions(fieldDataSchema.displayCondition, 
                    (condVal) => {
                        // console.log('Evaluating: Getting val for condition:', condVal, familyData);
                        if (typeof familyData?.[condVal.var] !== 'undefined') {
                            // console.log('Evaluating: Found val in family data:', familyData[condVal.var]);
                            return familyData[condVal.var]
                        } else {
                            const rootVals = props.customisationVars;
                            // console.log('Evaluating: Searching for val in rootVals:', rootVals, condVal.var);
                            // Otherwise search for the val elsewhere
                            const val = findVal(rootVals, condVal.var, null, null, props.index ?? 0);
                            if (typeof val !== 'undefined' && val !== null) {
                                // console.log('Evaluating: Found val in rootVals:', val);
                                return val;
                            }
                            return findVal(rootVals, condVal.var);
                        }
                    }
                );
                if (!shouldDisplay) {
                    // No need to check further, one failed condition is enough to hide the field
                    return false;
                }
            }
        }
    }
    return true;
}

export const evaluateDisplayConditionsForModule = (moduleData, node, campaignData, dataIndex = 0) => {
    // Modules can provide their own condition when an entire section (rather
    // than a single field) depends on a setting in the same repeatable item.
    const explicitConditions = moduleData?.moduleSettings?.displayCondition;
    if (Array.isArray(explicitConditions) && !evaluateDisplayConditions(explicitConditions, (condVal) =>
        findVal(node?.customisationVars || {}, condVal.var, null, null, dataIndex)
    )) {
        return false;
    }
    const displayConditionsLink = moduleData?.moduleSettings?.displayConditionsLink;
    if (displayConditionsLink && node && campaignData) {
        const displayConditionsArray = displayConditionsLink.split('.');
        // If the displayConditionsArray.length == 4 then we evaluate the same way as evaluateDisplayConditionsForField, if the length is 3 we evaluate the same way as evaluateDisplayConditionsForListNode
        if (displayConditionsArray.length === 3) {
            const groupSchema = getCustomisationFieldData(campaignData, node.featureData._id, displayConditionsLink);
            // console.log('Evaluating display conditions for node:', node, 'with group schema:', groupSchema);
            if (groupSchema?.displayCondition && Array.isArray(groupSchema.displayCondition)) {
                return evaluateDisplayConditions(groupSchema.displayCondition, 
                    (condVal) => {
                        // console.log('Evaluating: Getting val for condition:', condVal);
                        const val = findVal(node.customisationVars, condVal.var, null, null, 0);
                        if (typeof val !== 'undefined' && val !== null) {
                            // console.log('Evaluating: Found val:', val);
                            return val;
                        }
                        return null;
                    }
                );
            }
        } else if (displayConditionsArray.length === 4) {
            let shouldDisplay = true;
            // Split the dataLink and extract the customisation schema data for each step and log it out
            const dataLink = displayConditionsLink;
            if (dataLink) {
                if (!node?.featureData) {
                    return true;
                }
                const familyData = getGroupData(node.customisationVars, dataLink, 0, true) || {};
                const dataLinkParts = dataLink.split('.');
                for (let i = 0; i < dataLinkParts.length; i++) {
                    let path = '';
                    for (let j = 0; j <= i; j++) {
                        path += (j > 0 ? '.' : '') + dataLinkParts[j];
                    }
                    // console.log('CustomFieldRenderer DataLink Part:', path, getCustomisationFieldData(campaignData, node.featureData._id, path, 0, null));
                    const fieldDataSchema = getCustomisationFieldData(campaignData, node.featureData._id, path, 0, null);
                    // Check for any display conditions on this field
                    if (fieldDataSchema?.displayCondition && Array.isArray(fieldDataSchema.displayCondition)) {
                        shouldDisplay = evaluateDisplayConditions(fieldDataSchema.displayCondition, 
                            (condVal) => {
                                // console.log('Evaluating: Getting val for condition:', condVal, familyData);
                                if (typeof familyData?.[condVal.var] !== 'undefined') {
                                    // console.log('Evaluating: Found val in family data:', familyData[condVal.var]);
                                    return familyData[condVal.var]
                                } else {
                                    const rootVals = node.customisationVars;
                                    // console.log('Evaluating: Searching for val in rootVals:', rootVals, condVal.var);
                                    // Otherwise search for the val elsewhere
                                    const val = findVal(rootVals, condVal.var, null, null, 0);
                                    if (typeof val !== 'undefined' && val !== null) {
                                        // console.log('Evaluating: Found val in rootVals:', val);
                                        return val;
                                    }
                                    return findVal(rootVals, condVal.var);
                                }
                            }
                        );
                        if (!shouldDisplay) {
                            // No need to check further, one failed condition is enough to hide the field
                            return false;
                        }
                    }
                }
            }
        }
    }

    return true;
}

export const customFieldRenderer = (props) => {
    const { fieldData, moduleConfig, groupConfig, customisationVars } = props;
    // console.log('CustomFieldRenderer:', props, fieldData?.customFieldConfig?.customFieldType);
    
    // Split the dataLink and extract the customisation schema data for each step and log it out
    /* console.log('CustomFieldRenderer DataLink Part:', '-- Top --', getCustomisationFieldData(props.campaignData, props.selectedFeatureNode.featureData._id, '', props.index ?? 0, props.visualisationComponentKey ?? null));
    if (fieldData?.customFieldConfig?.dataLink) {
        const dataLinkParts = fieldData.customFieldConfig.dataLink.split('.');
        for (let i = 0; i < dataLinkParts.length; i++) {
            let path = '';
            for (let j = 0; j <= i; j++) {
                path += (j > 0 ? '.' : '') + dataLinkParts[j];
            }
            console.log('CustomFieldRenderer DataLink Part:', path, getCustomisationFieldData(props.campaignData, props.selectedFeatureNode.featureData._id, path, props.index ?? 0, props.visualisationComponentKey ?? null));
        }
    } */

    switch (fieldData?.customFieldConfig?.customFieldType.toLowerCase()) {
        case 'imageupload':
            return <ImageField {...props} />;
        case 'audioupload':
            return <AudioField {...props} />;
        case 'videoupload':
            return <VideoField {...props} />;
        case 'dropdown':
        case 'radio':
        case 'radiovertical':
            return <DropdownField {...props} />;
        case 'toggle':
            return <ToggleField {...props} />;
        case 'range':
            return <RangeSliderField {...props} showValue={props.isWide} />;
        case 'string':
            return <StringField {...props} />;
        case 'spamhashtag':
            return <SpamHashtagField {...props} />;
        case 'readonlyfield':
            return <ReadOnlyField {...props} />;
        case 'descriptionstring':
            return <NoteField {...props} />;
        case 'number':
            return <NumberField {...props} />;
        case 'rel-hms-time':
        case 'relhmstime':
            return <RelHmsTimeSelect {...props} />;
        case 'hms-time':
        case 'hmstime':
            return <HmsTimeSelect {...props} />;
        case 'ms-time':
        case 'mstime':
            return <MinSecTimeSelect {...props} />;
        case 'colourpicker':
            return <ColorPickerField {...props} />;
        case 'imagepreview':
            return <PreviewImageField {...props} />;
        case 'disruptor':
            return <DisruptorField {...props} />;
        case 'editdatafield':
            return <EditDataButton {...props} />;
        case 'textarea':
            return <TextAreaField {...props} />;
        case 'font':
            return <FontSelectField {...props} />;
        // Add more cases for different field types as needed
        default:
            console.warn('Unknown custom field type:', fieldData?.customFieldConfig?.customFieldType);
            return null;
    }
}

export const CustomFieldBlock = (props) => {
    const { customFields } = props;
    const containerRef = useRef(null);

    useEffect(() => {
        if (!props.selectedFieldDataLink || !containerRef.current) {
            return;
        }

        const target = Array.from(containerRef.current.querySelectorAll('[data-field-link]')).find(
            (element) => matchesSelectedFieldLink(props.selectedFieldDataLink, element.getAttribute('data-field-link') || '')
        );

        if (target) {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [props.selectedFieldDataLink, props.activeNode?.id, props.activeNode?.path, props.index, customFields.length]);

    /* const isOddLengthArray = () => {
        let length = 0;
        for (let i = 0; i < customFields.length; i++) {
            length++;
            // Wide fields cause our length to become even
            if (length % 2 === 1 && customFields[i].customFieldConfig?.isWide) {
                length++;
            }
        }
        return length % 2 === 1;
    } */
   let oddLengthCounter = 0;

    return (
         <div className='v2-module-columns-holder wrap' ref={containerRef}>
            {customFields.map((fieldData, index) => {
                // const oddLengthArray = isOddLengthArray();
                const shouldDisplay = evaluateDisplayConditionsForField(props, fieldData);
                const fieldDataLink = fieldData?.customFieldConfig?.dataLink ?? fieldData?.dataLink ?? '';
                const isHighlightedField = !!fieldDataLink && matchesSelectedFieldLink(props.selectedFieldDataLink, fieldDataLink);
                const isImageUploadField = fieldData?.customFieldConfig?.customFieldType?.toLowerCase?.() === 'imageupload';
                const shouldDisplayStyle = {
                    // opacity: shouldDisplay ? 1 : 0.25,
                    // pointerEvents: shouldDisplay ? 'auto' : 'none',
                }
                if (fieldData.customFieldConfig?.isShown === false || (!shouldDisplay && props.moduleConfig?.moduleSettings?.hideUnneededFields)) {
                    return null;
                }
                oddLengthCounter++;
                if (oddLengthCounter % 2 === 1 && fieldData.customFieldConfig?.isWide) {
                    oddLengthCounter++;
                }
                return (
                    <div
                        className={`v2-module-one-column${((/* oddLengthArray &&*/ index === customFields.length - 1 && oddLengthCounter % 2 === 1) || fieldData.customFieldConfig?.isWide) ? ' v2-full-width' : ''}${(isHighlightedField && !isImageUploadField) ? ' v2-field-highlight-target' : ''}`}
                        key={index}
                        style={shouldDisplayStyle}
                        data-field-link={fieldDataLink}
                    >
                        {customFieldRenderer({ ...props, fieldData, isEnabled: shouldDisplay, isWide: fieldData.customFieldConfig?.isWide ?? false, isHighlightedField })}
                    </div>
                );
            })}
        </div>
    );
}

export const renderFirstCustomBlock = (props) => {
    const moduleConfig = props.moduleConfig;
    const customFields = [];
    
    // Pull out all the custom fields until we reach a non-custom field, these will be rendered about the normal fields in a module
    for (let i = 0; i < moduleConfig?.fields?.length ?? 0; i++) {
        const fieldData = moduleConfig.fields[i];
        if (fieldData?.field === 'customCustomisationField' && fieldData?.customFieldConfig?.customFieldType) {
            customFields.push(fieldData);
        } else {
            break;
        }
    }

    if (customFields.length === 0) {
        return null;
    }

    return (
        <CustomFieldBlock customFields={customFields} {...props} />
    )
}

export const renderSecondCustomBlock = (props) => {
    const moduleConfig = props.moduleConfig;
    const customFields = [];

    // Ignore any custom fields until we hit a non-custom fields, collect all custom fields after that into our array
    let foundNonCustomField = false;
    for (let i = 0; i < moduleConfig?.fields?.length ?? 0; i++) {
        const fieldData = moduleConfig.fields[i];
        if (fieldData?.field === 'customCustomisationField' && fieldData?.customFieldConfig?.customFieldType) {
            if (foundNonCustomField) {
                customFields.push(fieldData);
            }
        } else {
            foundNonCustomField = true;
        }
    }

    if (customFields.length === 0) {
        return null;
    }

    return (
        <CustomFieldBlock customFields={customFields} {...props} />
    )
}
